package com.anjuros.website.redesign.sections.pricing

import androidx.compose.runtime.Composable
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.styles.PricingInfoButtonStyle
import com.anjuros.website.redesign.styles.PricingInfoCellStyle
import com.anjuros.website.redesign.styles.PricingUpdatePricesCloseStyle
import com.anjuros.website.redesign.styles.PricingUpdatePricesHeadStyle
import com.anjuros.website.redesign.styles.PricingUpdatePricesStyle
import com.anjuros.website.redesign.util.RdIcons
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.redesign.util.rdList
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.events.MouseEvent
import kotlin.math.max

private const val POPOVER_ID = "update-prices"

/**
 * "Paid per update" with an info button that opens the Basic plan's update price list.
 * With a mouse it opens on hover (or click) under the button. On touch screens a tap opens it centred over a
 * dimmed page that can't scroll behind it, closed with the X, a tap outside or Escape. It lives in the browser's
 * popover layer, so the comparison table's sideways scrolling can't clip it.
 */
@Composable
fun BasicUpdatesCell() {
    val label = LocalText(RdText.UPDATE_PRICES_LABEL)
    val closeLabel = LocalText(RdText.UPDATE_PRICES_CLOSE)
    val title = LocalText(RdText.UPDATE_PRICES_TITLE)

    Span(PricingInfoCellStyle.toModifier().toAttrs {
        ref { wrapper ->
            val dispose = wireUpdatePrices(wrapper)
            onDispose { dispose() }
        }
    }) {
        Text(LocalText(RdText.COMPARE_UPDATES_BASIC))
        Button(
            PricingInfoButtonStyle.toModifier().attrsModifier {
                attr("type", "button")
                attr("aria-label", label)
                attr("aria-controls", POPOVER_ID)
                attr("aria-expanded", "false")
                attr("data-role", "open")
            }.toAttrs()
        ) { RdIcons.Info(size = 18) }

        Div(
            PricingUpdatePricesStyle.toModifier().id(POPOVER_ID).attrsModifier {
                attr("popover", "manual")
                attr("role", "dialog")
                attr("aria-label", title)
            }.toAttrs()
        ) {
            Div(PricingUpdatePricesHeadStyle.toModifier().toAttrs()) {
                B { Text(title) }
                Button(
                    PricingUpdatePricesCloseStyle.toModifier().attrsModifier {
                        attr("type", "button")
                        attr("aria-label", closeLabel)
                        attr("data-role", "close")
                    }.toAttrs()
                ) { RdIcons.Close(size = 18) }
            }
            P { Text(LocalText(RdText.UPDATE_PRICES_LEDE)) }
            Ul {
                rdList(LocalText(RdText.UPDATE_PRICES_ROWS)).forEach { row ->
                    val (item, price) = row.split(";").let { it[0] to it.getOrElse(1) { "" } }
                    Li {
                        Span { Text(item) }
                        B { Text(price) }
                    }
                }
            }
            P { Text(LocalText(RdText.UPDATE_PRICES_NOTE)) }
        }
    }
}

/** Opens, closes and positions the price list. Returns a function that removes every listener. */
private fun wireUpdatePrices(wrapper: HTMLElement): () -> Unit {
    val button = wrapper.querySelector("[data-role=open]") as? HTMLElement ?: return {}
    val popover = wrapper.querySelector("[popover]") as? HTMLElement ?: return {}
    val close = popover.querySelector("[data-role=close]") as? HTMLElement ?: return {}
    // Browsers without the popover API keep it hidden (see the style), so do nothing there.
    if (popover.asDynamic().showPopover == undefined) return {}

    fun canHover() = window.matchMedia("(hover: hover) and (pointer: fine)").matches
    // Touch screens get the centred, dimmed version; decided each time it opens.
    var modal = false
    var pinned = false
    var hideTimer = 0
    val root = document.documentElement as? HTMLElement
    val body = document.body

    fun isOpen(): Boolean = popover.matches(":popover-open")

    fun place() {
        val width = popover.offsetWidth.toDouble()
        val height = popover.offsetHeight.toDouble()
        val viewportWidth = document.documentElement?.clientWidth?.toDouble() ?: window.innerWidth.toDouble()
        val viewportHeight = window.innerHeight.toDouble()
        val gap = 8.0
        if (modal) {
            popover.style.left = "${max(gap, (viewportWidth - width) / 2)}px"
            popover.style.top = "${max(gap, (viewportHeight - height) / 2)}px"
            return
        }
        val anchor = button.getBoundingClientRect()
        val left = (anchor.left + anchor.width / 2 - width / 2).coerceIn(gap, max(gap, viewportWidth - width - gap))
        val below = anchor.bottom + gap
        val above = anchor.top - gap - height
        // Below the button if it fits, else above; on short screens, as low as it goes while staying on screen.
        val top = when {
            below + height <= viewportHeight - gap -> below
            above >= gap -> above
            else -> max(gap, viewportHeight - height - gap)
        }
        popover.style.left = "${left}px"
        popover.style.top = "${top}px"
    }

    // While open on a touch screen, only the price list scrolls, not the page behind it.
    val onTouchMove: (Event) -> Unit = { evt ->
        if (!popover.contains(evt.target as? Node)) evt.preventDefault()
    }
    val noPassive: dynamic = js("({ passive: false })")

    fun lockPage(locked: Boolean) {
        if (!modal) return
        if (locked) {
            root?.style?.setProperty("overflow", "hidden")
            body?.style?.setProperty("overflow", "hidden")
            document.addEventListener("touchmove", onTouchMove, noPassive)
        } else {
            root?.style?.removeProperty("overflow")
            body?.style?.removeProperty("overflow")
            document.removeEventListener("touchmove", onTouchMove, noPassive)
        }
    }

    fun show() {
        window.clearTimeout(hideTimer)
        if (!isOpen()) {
            modal = !canHover()
            if (modal) popover.setAttribute("data-modal", "") else popover.removeAttribute("data-modal")
            popover.asDynamic().showPopover()
            lockPage(true)
        }
        button.setAttribute("aria-expanded", "true")
        place()
    }

    fun hide() {
        window.clearTimeout(hideTimer)
        pinned = false
        if (isOpen()) {
            popover.asDynamic().hidePopover()
            lockPage(false)
        }
        modal = false
        button.setAttribute("aria-expanded", "false")
    }

    fun hideSoon() {
        if (pinned) return
        window.clearTimeout(hideTimer)
        // A short delay lets the mouse travel from the button into the price list.
        hideTimer = window.setTimeout({ hide() }, 150)
    }

    val onButtonClick: (Event) -> Unit = {
        if (isOpen() && (pinned || modal)) hide() else { pinned = true; show() }
    }
    val onCloseClick: (Event) -> Unit = {
        hide()
        button.focus()
    }
    // Hover only with a mouse; phones can fake mouse events on tap.
    val onEnter: (Event) -> Unit = { if (canHover() && !modal) show() }
    val onLeave: (Event) -> Unit = { if (!modal) hideSoon() }
    // A press outside the box closes it. Taps on the dimmed backdrop are reported on the box itself,
    // so the press position decides.
    val onPointerDownOutside: (Event) -> Unit = pointerDown@{ evt ->
        if (!isOpen() || button.contains(evt.target as? Node)) return@pointerDown
        val box = popover.getBoundingClientRect()
        val press = evt as MouseEvent
        val inside = press.clientX >= box.left && press.clientX <= box.right && press.clientY >= box.top && press.clientY <= box.bottom
        if (!inside) hide()
    }
    val onKeyDown: (Event) -> Unit = { evt ->
        if ((evt as KeyboardEvent).key == "Escape" && isOpen()) {
            hide()
            button.focus()
        }
    }
    val onMove: (Event) -> Unit = { if (isOpen()) place() }

    button.addEventListener("click", onButtonClick)
    close.addEventListener("click", onCloseClick)
    button.addEventListener("mouseenter", onEnter)
    button.addEventListener("mouseleave", onLeave)
    popover.addEventListener("mouseenter", onEnter)
    popover.addEventListener("mouseleave", onLeave)
    document.addEventListener("pointerdown", onPointerDownOutside)
    document.addEventListener("keydown", onKeyDown)
    // Capture, so scrolling the table sideways also moves the price list with the button.
    window.addEventListener("scroll", onMove, true)
    window.addEventListener("resize", onMove)

    return {
        window.clearTimeout(hideTimer)
        if (isOpen()) lockPage(false)
        button.removeEventListener("click", onButtonClick)
        close.removeEventListener("click", onCloseClick)
        button.removeEventListener("mouseenter", onEnter)
        button.removeEventListener("mouseleave", onLeave)
        popover.removeEventListener("mouseenter", onEnter)
        popover.removeEventListener("mouseleave", onLeave)
        document.removeEventListener("pointerdown", onPointerDownOutside)
        document.removeEventListener("keydown", onKeyDown)
        window.removeEventListener("scroll", onMove, true)
        window.removeEventListener("resize", onMove)
    }
}
